import { useState } from "react";
import axios from "axios";
import { CheckCircle, XCircle, WarningCircle, CircleNotch, ArrowRight, DownloadSimple, Clipboard } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERDICT_STYLES = {
  compliant: "bg-green-50 text-green-700 border-green-300 dark:bg-green-950/30 dark:text-green-400 dark:border-green-800",
  borderline: "bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800",
  non_compliant: "bg-red-50 text-red-700 border-red-300 dark:bg-red-950/30 dark:text-red-400 dark:border-red-800",
};
const VERDICT_LABELS = { compliant: "Compliant", borderline: "Borderline", non_compliant: "Non-Compliant" };

function StatusIcon({ status, size = 14 }) {
  if (status === "pass") return <CheckCircle size={size} weight="fill" className="text-green-600 dark:text-green-400 shrink-0" />;
  if (status === "borderline") return <WarningCircle size={size} weight="fill" className="text-amber-600 dark:text-amber-400 shrink-0" />;
  return <XCircle size={size} weight="fill" className="text-red-600 dark:text-red-400 shrink-0" />;
}

const SAMPLE = "AAPL, MSFT, GOOGL, AMZN, TSLA, NVDA, META, JPM, V, JNJ";

export default function PortfolioScreener({ onSelectStock }) {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  const handleScreen = async () => {
    const tickers = input
      .split(/[\s,;]+/)
      .map((t) => t.trim().toUpperCase())
      .filter((t) => t.length >= 1 && t.length <= 5 && /^[A-Z]+$/.test(t));

    if (tickers.length === 0) {
      setError("Enter at least one valid ticker symbol (e.g., AAPL, MSFT)");
      return;
    }
    if (tickers.length > 30) {
      setError("Maximum 30 tickers per portfolio screen");
      return;
    }

    setLoading(true);
    setError("");
    setReport(null);
    try {
      const res = await axios.post(`${API}/portfolio/screen`, { tickers });
      setReport(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to screen portfolio");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") handleScreen();
  };

  const handleExportReport = () => {
    if (!report) return;
    const lines = [
      "Ticker,Company,Sector,Verdict,Debt/MCap (%),Threshold,Interest/Rev (%),Threshold,Receivables/Assets (%),Threshold,Business Activity",
      ...report.results.map((s) =>
        [
          s.ticker, `"${s.company_name}"`, `"${s.sector}"`, s.verdict,
          s.debt_ratio, "33%", s.interest_ratio, "5%", s.receivable_ratio, "49%",
          s.business_status === "pass" ? "Permissible" : "Prohibited",
        ].join(",")
      ),
    ];
    if (report.errors?.length) {
      lines.push("", "Not Found:");
      report.errors.forEach((t) => lines.push(t));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "portfolio_compliance_report.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const s = report?.summary;

  return (
    <div className="py-6" data-testid="portfolio-screener">
      {/* Input Section */}
      {!report && (
        <div className="max-w-2xl" data-testid="portfolio-input-section">
          <h2 className="text-lg font-medium text-zinc-950 dark:text-zinc-100 mb-1">
            Screen Your Portfolio
          </h2>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-5">
            Paste your tickers below (comma or space separated, max 30) to get a bulk halal compliance report.
          </p>

          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="AAPL, MSFT, GOOGL, TSLA, AMZN..."
            rows={3}
            className="w-full border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-4 py-3 text-sm font-mono rounded-none shadow-none focus:outline-none focus:ring-1 focus:ring-zinc-950 dark:focus:ring-zinc-400 placeholder:text-zinc-300 dark:placeholder:text-zinc-600 text-zinc-950 dark:text-zinc-100 resize-none"
            data-testid="portfolio-input"
          />

          <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
            <button
              onClick={() => setInput(SAMPLE)}
              className="text-[11px] font-mono text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 flex items-center gap-1 transition-colors"
              data-testid="portfolio-sample-btn"
            >
              <Clipboard size={12} weight="bold" />
              Try sample portfolio
            </button>

            <button
              onClick={handleScreen}
              disabled={loading || !input.trim()}
              className="flex items-center gap-2 px-5 py-2 text-[11px] font-mono uppercase tracking-wider bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-700 dark:hover:bg-zinc-300 transition-colors disabled:opacity-40"
              data-testid="portfolio-screen-btn"
            >
              {loading ? (
                <CircleNotch size={14} weight="bold" className="animate-spin" />
              ) : (
                <ArrowRight size={14} weight="bold" />
              )}
              {loading ? "Screening..." : "Screen Portfolio"}
            </button>
          </div>

          {error && (
            <p className="text-xs font-mono text-red-500 mt-3" data-testid="portfolio-error">{error}</p>
          )}

          <p className="text-[10px] font-mono text-zinc-300 dark:text-zinc-600 mt-4">
            Press Ctrl+Enter to submit. Each ticker is screened against AAOIFI thresholds (Debt/MCap &lt; 33%, Interest/Rev &lt; 5%, Receivables/Assets &lt; 49%).
          </p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="py-16 text-center" data-testid="portfolio-loading">
          <div className="inline-block w-6 h-6 border-2 border-zinc-200 dark:border-zinc-700 border-t-zinc-950 dark:border-t-zinc-100 rounded-full animate-spin" />
          <p className="text-xs font-mono text-zinc-400 dark:text-zinc-500 mt-3">
            Screening {input.split(/[\s,;]+/).filter(Boolean).length} tickers against AAOIFI thresholds...
          </p>
        </div>
      )}

      {/* Report */}
      {report && !loading && (
        <div data-testid="portfolio-report">
          {/* Summary Bar */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <h2 className="text-lg font-medium text-zinc-950 dark:text-zinc-100">
              Portfolio Compliance Report
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportReport}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors"
                data-testid="portfolio-export-btn"
              >
                <DownloadSimple size={14} weight="bold" />
                Export Report
              </button>
              <button
                onClick={() => { setReport(null); setInput(""); }}
                className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors"
                data-testid="portfolio-new-btn"
              >
                New Screen
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="portfolio-summary">
            <div className="border border-zinc-200 dark:border-zinc-800 p-4">
              <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500">Total Screened</p>
              <p className="text-2xl font-light font-mono text-zinc-950 dark:text-zinc-100 mt-1">{s.total}</p>
            </div>
            <div className="border border-zinc-200 dark:border-zinc-800 p-4">
              <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500">Compliant</p>
              <p className="text-2xl font-light font-mono text-green-600 dark:text-green-400 mt-1">{s.compliant}</p>
              <p className="text-[10px] font-mono text-zinc-400 dark:text-zinc-500">{s.compliant_pct}%</p>
            </div>
            <div className="border border-zinc-200 dark:border-zinc-800 p-4">
              <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500">Borderline</p>
              <p className="text-2xl font-light font-mono text-amber-600 dark:text-amber-400 mt-1">{s.borderline}</p>
              <p className="text-[10px] font-mono text-zinc-400 dark:text-zinc-500">{s.borderline_pct}%</p>
            </div>
            <div className="border border-zinc-200 dark:border-zinc-800 p-4">
              <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500">Non-Compliant</p>
              <p className="text-2xl font-light font-mono text-red-600 dark:text-red-400 mt-1">{s.non_compliant}</p>
              <p className="text-[10px] font-mono text-zinc-400 dark:text-zinc-500">{s.non_compliant_pct}%</p>
            </div>
          </div>

          {/* Compliance Bar */}
          {s.total > 0 && (
            <div className="mb-6" data-testid="portfolio-compliance-bar">
              <div className="flex h-3 overflow-hidden">
                {s.compliant > 0 && (
                  <div className="bg-green-500" style={{ width: `${s.compliant_pct}%` }} title={`Compliant: ${s.compliant_pct}%`} />
                )}
                {s.borderline > 0 && (
                  <div className="bg-amber-500" style={{ width: `${s.borderline_pct}%` }} title={`Borderline: ${s.borderline_pct}%`} />
                )}
                {s.non_compliant > 0 && (
                  <div className="bg-red-500" style={{ width: `${s.non_compliant_pct}%` }} title={`Non-Compliant: ${s.non_compliant_pct}%`} />
                )}
              </div>
              <div className="flex items-center gap-4 mt-2">
                <span className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-500 dark:text-zinc-400">
                  <span className="w-2.5 h-2.5 bg-green-500 inline-block" /> Compliant {s.compliant_pct}%
                </span>
                <span className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-500 dark:text-zinc-400">
                  <span className="w-2.5 h-2.5 bg-amber-500 inline-block" /> Borderline {s.borderline_pct}%
                </span>
                <span className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-500 dark:text-zinc-400">
                  <span className="w-2.5 h-2.5 bg-red-500 inline-block" /> Non-Compliant {s.non_compliant_pct}%
                </span>
              </div>
            </div>
          )}

          {/* Per-Stock Results */}
          <div className="space-y-0" data-testid="portfolio-results-list">
            {report.results.map((stock) => (
              <div
                key={stock.ticker}
                className="border-b border-zinc-100 dark:border-zinc-800/50 py-4 cursor-pointer hover:bg-zinc-50/60 dark:hover:bg-zinc-900/40 transition-colors -mx-2 px-2"
                onClick={() => onSelectStock(stock)}
                data-testid={`portfolio-result-${stock.ticker}`}
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-mono font-medium text-zinc-950 dark:text-zinc-100">{stock.ticker}</span>
                    <span className="text-sm text-zinc-500 dark:text-zinc-400 truncate">{stock.company_name}</span>
                    <span className="text-[10px] font-mono text-zinc-300 dark:text-zinc-600 hidden sm:inline">{stock.sector}</span>
                  </div>
                  <span className={`px-2.5 py-0.5 text-[9px] font-mono font-bold uppercase border tracking-widest shrink-0 ${VERDICT_STYLES[stock.verdict] || VERDICT_STYLES.non_compliant}`}>
                    {VERDICT_LABELS[stock.verdict] || "N/A"}
                  </span>
                </div>
                <div className="flex items-center gap-4 sm:gap-6 text-[11px] font-mono text-zinc-500 dark:text-zinc-400 flex-wrap">
                  <span className="flex items-center gap-1">
                    <StatusIcon status={stock.debt_status} size={12} />
                    Debt/MCap {stock.debt_ratio}%
                    <span className="text-zinc-300 dark:text-zinc-600">/33%</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <StatusIcon status={stock.interest_status} size={12} />
                    Int./Rev {stock.interest_ratio}%
                    <span className="text-zinc-300 dark:text-zinc-600">/5%</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <StatusIcon status={stock.receivable_status} size={12} />
                    AR/Assets {stock.receivable_ratio}%
                    <span className="text-zinc-300 dark:text-zinc-600">/49%</span>
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Errors */}
          {report.errors?.length > 0 && (
            <div className="mt-4 border border-zinc-200 dark:border-zinc-800 p-4" data-testid="portfolio-errors">
              <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500 mb-2">
                Not Found ({report.errors.length})
              </p>
              <p className="text-sm font-mono text-zinc-500 dark:text-zinc-400">
                {report.errors.join(", ")}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
